import classNames from "classnames";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import PublishBlock from "components/PublishBlock";

import "./navigation-header.scss";
import { isAbsoluteUrl } from "./utils";

const RightBlock = ({
  isDraftBlockHidden,
  previewDraftUrl,
  isResetDraftButtonVisible,
  onResetClick,
  isResetting,
  isPublishDisabled,
  isPublishing,
  handlePublish,
  isPublishPreviewDisabled,
  previewPublishedUrl,
  isPublishButtonVisible,
  isResetAlertOpen,
  setIsResetAlertOpen,
  handleReset,
  publishAlertTitle,
  publishAlertDescription,
  publishButtonProps = {},
}) => {
  const { t } = useTranslation();

  const renderDraftButtons = ({ ViewDraftButton, ResetDraftButton }) =>
    !isDraftBlockHidden && (
      <>
        <ViewDraftButton
          {...(isAbsoluteUrl(previewDraftUrl)
            ? { href: previewDraftUrl }
            : { to: previewDraftUrl })}
        />
        {isResetDraftButtonVisible && (
          <ResetDraftButton
            loading={isResetting}
            onClick={() => onResetClick()}
          />
        )}
      </>
    );

  const renderPublishButtons = ({ PublishButton, PublishPreviewButton }) => (
    <>
      {isPublishButtonVisible && (
        <PublishButton
          disabled={isPublishDisabled}
          loading={isPublishing}
          onClick={() => handlePublish()}
          {...publishButtonProps}
        />
      )}
      <PublishPreviewButton
        {...(isAbsoluteUrl(previewPublishedUrl)
          ? { href: previewPublishedUrl }
          : { to: previewPublishedUrl })}
        disabled={isPublishPreviewDisabled}
        className={classNames({
          "standalone-publish-preview-button": !isPublishButtonVisible,
        })}
      />
    </>
  );

  return (
    <>
      <PublishBlock {...{ renderDraftButtons, renderPublishButtons }} />
      <PublishBlock.Alert
        isOpen={isResetAlertOpen}
        isSubmitting={isResetting}
        message={
          publishAlertDescription ||
          t("neetoMolecules.navigationHeader.resetDescription")
        }
        title={
          publishAlertTitle || t("neetoMolecules.navigationHeader.resetTitle")
        }
        onClose={() => setIsResetAlertOpen(false)}
        onSubmit={handleReset}
      />
    </>
  );
};

RightBlock.propTypes = {
  /**
   * To show/hide the draft block.
   */
  isDraftBlockHidden: PropTypes.bool,
  /**
   * Draft preview URL, when the draft button is clicked user will be redirected to this URL.
   */
  previewDraftUrl: PropTypes.string,
  /**
   * Boolean to control the visibility of reset draft button.
   */
  isResetDraftButtonVisible: PropTypes.bool,
  /**
   * Callback function that will be called when the reset button is clicked.
   */
  onResetClick: PropTypes.func,
  /**
   * Setting this to `true` will show a spinner on the reset button. Use when the reset process is in progress.
   */
  isResetting: PropTypes.bool,
  /**
   * To disable the publish button.
   */
  isPublishDisabled: PropTypes.bool,
  /**
   * Setting this to `true` will show a spinner on the publish button. Use when the publish process is in progress.
   */
  isPublishing: PropTypes.bool,
  /**
   * Callback function that will be called when the publish button is clicked.
   */
  handlePublish: PropTypes.func,
  /**
   * To disable the publish preview button.
   */
  isPublishPreviewDisabled: PropTypes.bool,
  /**
   * Publish preview URL, when the preview publish button is clicked user will be redirected to this URL.
   */
  previewPublishedUrl: PropTypes.string,
  /**
   * Boolean to control whether the user have the ability to publish.
   */
  isPublishButtonVisible: PropTypes.bool,
  /**
   * Boolean to control whether the reset alert is open.
   */
  isResetAlertOpen: PropTypes.bool,
  /**
   * Handler to control the `isResetAlert` state variable.
   */
  setIsResetAlertOpen: PropTypes.func,
  /**
   * Callback function that will be called when the reset button is clicked.
   */
  handleReset: PropTypes.func,
  /**
   * Description to be shown in the publish alert
   */
  publishAlertDescription: PropTypes.string,
  /**
   * Title of the publish alert
   */
  publishAlertTitle: PropTypes.string,
  /**
   * Props to be passed to the publish button
   */
  publishButtonProps: PropTypes.object,
};

export default RightBlock;
